import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { InteractionStatus } from "@azure/msal-browser"
import { useIsAuthenticated, useMsal } from "@azure/msal-react"
import { useIdleTimer } from "react-idle-timer"
import { useLocation, useNavigate } from "react-router-dom"
import { sendTealiumEvent } from "tealium"

import { Authenticated } from "components/Authenticated"
import { LoadingScreen } from "components/LoadingScreen"

import { AuthApi } from "api/auth"
import { UserApi } from "api/user"

import { AuthContext } from "context/AuthContext"
import { ClientContext } from "context/ClientContext"
import { useErrorModal } from "context/ErrorContext"
import { UserContext } from "context/UserContext"

import { acquireToken } from "utilities/acquireToken"
import { AppRoutes, IDLE_TIMEOUT } from "utilities/constants"
import { log, SHOW_LOGS } from "utilities/logging"
import {
  getSignInAuthority,
  getSignUpAuthority,
  parseMsalConfig,
} from "utilities/msalConfig"

import { SessionTimeoutModal } from "./SessionTimeoutModal"
import { Unauthenticated } from "./Unauthenticated"

export const AuthenticationWrapper = () => {
  const [errorLoadingToken, setErrorLoadingToken] = useState<boolean>(false)
  const { adb2cToken, token, setToken, setAdb2cToken, unsetTokens } =
    useContext(AuthContext)
  const { user, setUser, cid } = useContext(UserContext)
  const { client } = useContext(ClientContext)
  const navigate = useNavigate()
  const location = useLocation()

  const { handleError } = useErrorModal()

  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()

  const [showInactivityWarning, setShowInactivityWarning] = useState(false)
  const idleTimerRef = useRef<any>(null)

  const authRequestInFlightRef = useRef<boolean>(false)
  const userRequestInFlightRef = useRef<boolean>(false)
  const tokenAcquisitionInFlightRef = useRef<boolean>(false)

  const onIdle = () => {
    setShowInactivityWarning(true)
  }

  const handleStayActive = useCallback(() => {
    setShowInactivityWarning(false)
    if (idleTimerRef.current) {
      idleTimerRef.current.reset()
    }
  }, [])

  const handleCountdownComplete = useCallback(() => {
    unsetTokens()
    setUser(undefined)

    navigate(`/${AppRoutes.logout}`)
  }, [unsetTokens, setUser, navigate])

  useIdleTimer({
    onIdle,
    timeout: IDLE_TIMEOUT,
    ref: idleTimerRef,
  })

  useEffect(() => {
    const handleStayActiveEvent = () => handleStayActive()
    window.addEventListener("timeout-warning", handleStayActiveEvent)
    return () =>
      window.removeEventListener("timeout-warning", handleStayActiveEvent)
  }, [handleStayActive])

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) {
      log(SHOW_LOGS.ADB2C_TOKEN, `Waiting for MSAL: inProgress = ${inProgress}`)
      return
    }

    if (location.pathname === `/${AppRoutes.loggedOut}`) {
      return
    }

    if (location.pathname === `/${AppRoutes.logout}`) {
      return
    }
    
    if (!isAuthenticated && client && instance) {
      log(
        SHOW_LOGS.ADB2C_TOKEN,
        "No Azure B2C authentication, redirecting to sign up",
      )

      const config = parseMsalConfig(client)

      const authority =
        location.pathname === `/${AppRoutes.authSignIn}`
          ? getSignInAuthority(config.authority)
          : getSignUpAuthority(config.authority)

      instance
        .loginRedirect({
          scopes: ["openid", "offline_access"],
          authority,
          redirectStartPage: window.location.origin,
          redirectUri: window.location.origin,
        })
        .catch(err => {
          if (!err.message?.includes("interaction_in_progress")) {
            log(SHOW_LOGS.ADB2C_TOKEN, "Login redirect failed", err)
          }
        })
    }
  }, [isAuthenticated, inProgress, client, instance, location.pathname])

  useEffect(() => {
    const isOnPublicAuthRoute =
      location.pathname === `/${AppRoutes.loggedOut}` ||
      location.pathname === `/${AppRoutes.logout}`

    if (isOnPublicAuthRoute) {
      return
    }

    if (adb2cToken) {
      return
    }

    if (!isAuthenticated || !instance) {
      return
    }

    if (inProgress !== InteractionStatus.None) {
      log(SHOW_LOGS.ADB2C_TOKEN, `Waiting for MSAL interaction: ${inProgress}`)
      return
    }

    if (tokenAcquisitionInFlightRef.current) {
      return
    }
    tokenAcquisitionInFlightRef.current = true

    log(SHOW_LOGS.ADB2C_TOKEN, "Subsequent page load - acquiring token silently")
    acquireToken(instance, {
      setToken,
      setAdb2cToken,
      setErrorLoadingToken,
      handleError,
    }).finally(() => {
      tokenAcquisitionInFlightRef.current = false
    })
  }, [
    isAuthenticated,
    inProgress,
    instance,
    adb2cToken,
    setToken,
    setAdb2cToken,
    handleError,
    location.pathname,
  ])

  useEffect(() => {
    if (
      !errorLoadingToken &&
      !token &&
      adb2cToken &&
      cid &&
      !authRequestInFlightRef.current
    ) {
      authRequestInFlightRef.current = true

      log(SHOW_LOGS.ADB2C_TOKEN, "Exchanging Azure B2C token for backend JWT")

      AuthApi.authenticate({ idToken: adb2cToken, cid })
        .then(token => {
          log(SHOW_LOGS.ADB2C_TOKEN, "Got backend JWT")
          setToken(token.idToken)
        })
        .catch(e => {
          log(SHOW_LOGS.ADB2C_TOKEN, "Token exchange failed:", e)
          setErrorLoadingToken(true)
          handleError({
            action: "logout",
            log: { message: "Token exchange failed", error: e },
          })
        })
        .finally(() => {
          authRequestInFlightRef.current = false
        })
    }
  }, [
    adb2cToken,
    errorLoadingToken,
    token,
    cid,
    setToken,
    setAdb2cToken,
    handleError,
  ])

  useEffect(() => {
    if (token && !user && !userRequestInFlightRef.current) {
      userRequestInFlightRef.current = true

      log(SHOW_LOGS.USER_TOKEN, "Loading user data with backend JWT")

      UserApi.me({ token })
        .then(user => {
          log(SHOW_LOGS.USER_TOKEN, "User loaded successfully")
          setUser(user)
        })
        .catch(error => {
          log(SHOW_LOGS.USER_TOKEN, "Failed to load user:", error)
          handleError({
            action: "logout",
            log: { message: ".me has failed", error },
          })
        })
        .finally(() => {
          userRequestInFlightRef.current = false
        })
    }
  }, [token, user, handleError, setUser])

  useEffect(() => {
    if (user) {
      sendTealiumEvent("app_login", {
        type: "link",
        email_address: user.email,
      })
    }
  }, [user])

  const isMsalNotReady = client && !instance
  const isMsalProcessing = inProgress !== InteractionStatus.None

  const isPublicRoute =
    location.pathname === `/${AppRoutes.loggedOut}` ||
    location.pathname === `/${AppRoutes.logout}`

  const isBackendLoading =
    isAuthenticated && (!token || !user) && !errorLoadingToken && !isPublicRoute

  const isRedirecting = !isAuthenticated && !isPublicRoute && !errorLoadingToken

  if (
    isMsalNotReady ||
    isMsalProcessing ||
    isBackendLoading ||
    isRedirecting ||
    errorLoadingToken
  ) {
    log(
      SHOW_LOGS.ADB2C_TOKEN,
      `Auth loading: msalReady=${!isMsalNotReady}, processing=${isMsalProcessing}, backendLoading=${isBackendLoading}, redirecting=${isRedirecting}, error=${errorLoadingToken}`,
    )

    return (
      <LoadingScreen
        data-testid="loading-screen-from-authentication-wrapper"
        show={true}
      />
    )
  }

  if (token && user) {
    log(SHOW_LOGS.USER_EMAIL, `Authenticated: ${user.email}`)
    return (
      <>
        <SessionTimeoutModal
          isOpen={showInactivityWarning}
          countdown={60}
          onStayActive={handleStayActive}
          onCountdownComplete={handleCountdownComplete}
        />
        <Authenticated />
      </>
    )
  }

  return (
    <>
      <SessionTimeoutModal
        isOpen={showInactivityWarning}
        countdown={30}
        onStayActive={handleStayActive}
        onCountdownComplete={handleCountdownComplete}
      />
      <Unauthenticated />
    </>
  )
}
